import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';

// Categories mirror FeedbackWidget exactly so emails are routed identically.
const CATEGORIES = [
  { id: 'content', label: 'CHECKLIST ERROR',  color: 'var(--gold)', rgb: '--gold-rgb' },
  { id: 'feature', label: 'MISSING SET / ADD', color: 'var(--cyan)', rgb: '--cyan-rgb' },
  { id: 'bug',     label: 'BUG REPORT',       color: 'var(--red)', rgb: '--red-rgb' },
  { id: 'other',   label: 'OTHER',            color: 'var(--green)', rgb: '--green-rgb' },
];

const C = {
  uiBg: 'var(--bg)',
  panel: 'var(--panel-deep)',
  border: 'var(--border)',
  borderSoft: 'rgba(var(--slate-rgb),0.45)',
  ink: 'var(--text-bright)',
  inkSoft: 'var(--muted-8)',
  inkFaint: '#5a6680',
  cyan: 'var(--cyan)',
  green: 'var(--green)',
  gold: 'var(--gold)',
};

export default function SuggestEditModal({ isOpen, onClose, user, context }) {
  const [category, setCategory] = useState('content');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  // Reset state every time the modal closes
  useEffect(() => {
    if (!isOpen) {
      // delay reset so the close animation looks clean
      const t = setTimeout(() => {
        setCategory('content');
        setMessage('');
        setLoading(false);
        setDone(false);
        setError(null);
      }, 200);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  // Lock body scroll while open
  useEffect(() => {
    if (isOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (!message.trim()) return;
    setLoading(true);
    setError(null);
    try {
      // Pre-pend the context to the message so it's visible in feedback emails
      const fullMessage = context
        ? `[Re: ${context}]\n\n${message.trim()}`
        : message.trim();

      const { error: insertError } = await supabase.from('feedback').insert({
        user_id: user?.id || null,
        email: user?.email || null,
        category,
        message: fullMessage,
        page: window.location.pathname,
        created_at: new Date().toISOString(),
      });

      if (insertError) {
        setError('Could not send. Please try again.');
        setLoading(false);
        return;
      }

      setLoading(false);
      setDone(true);
      setTimeout(() => {
        onClose();
      }, 2200);
    } catch (e) {
      setError('Could not send. Please try again.');
      setLoading(false);
    }
  };

  const onBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <>
      {/* Inline keyframes for the modal entrance */}
      <style>{`
        @keyframes ss-modal-fade  { from { opacity: 0; }                                  to { opacity: 1; } }
        @keyframes ss-modal-slide { from { opacity: 0; transform: translateY(12px); }     to { opacity: 1; transform: translateY(0); } }
      `}</style>

      {/* Backdrop */}
      <div
        onClick={onBackdropClick}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          padding: '60px 20px 20px',
          overflowY: 'auto',
          animation: 'ss-modal-fade 0.18s ease-out',
        }}
      >
        {/* Modal panel */}
        <div style={{
          background: C.panel,
          border: `1px solid ${C.border}`,
          borderRadius: '6px',
          width: '100%',
          maxWidth: '480px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
          animation: 'ss-modal-slide 0.22s ease-out',
        }}>

          {done ? (
            <div style={{ padding: '40px 24px', textAlign: 'center' }}>
              <div style={{ fontSize: '40px', color: C.green, marginBottom: '12px', lineHeight: 1 }}>✓</div>
              <div style={{ fontFamily: "'Orbitron', monospace", fontSize: '14px', letterSpacing: '2px', color: C.green, marginBottom: '6px' }}>
                THANKS FOR THE FEEDBACK
              </div>
              <div style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: '12px', color: C.inkSoft }}>
                We'll take a look.
              </div>
            </div>
          ) : (
            <>
              {/* Header */}
              <div style={{
                padding: '18px 20px 14px',
                borderBottom: `1px solid ${C.border}`,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: '12px',
              }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontFamily: "'Orbitron', monospace", fontSize: '11px', letterSpacing: '2.5px', color: C.cyan, marginBottom: '4px' }}>
                    SUGGEST AN EDIT
                  </div>
                  {context && (
                    <div style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: '11px', color: C.inkSoft, lineHeight: 1.4, wordBreak: 'break-word' }}>
                      Re: {context}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Close"
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: C.inkFaint,
                    fontSize: '18px',
                    cursor: 'pointer',
                    padding: '0 4px',
                    lineHeight: 1,
                    flexShrink: 0,
                  }}
                >✕</button>
              </div>

              {/* Categories */}
              <div style={{
                padding: '14px 20px',
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '6px',
              }}>
                {CATEGORIES.map(c => {
                  const active = category === c.id;
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setCategory(c.id)}
                      style={{
                        fontFamily: "'Orbitron', monospace",
                        fontSize: '9.5px',
                        letterSpacing: '1.5px',
                        padding: '9px 10px',
                        background: active ? `rgba(var(${c.rgb}),0.08)` : 'transparent',
                        border: `1px solid ${active ? c.color : C.border}`,
                        color: active ? c.color : C.inkSoft,
                        cursor: 'pointer',
                        borderRadius: '2px',
                        textAlign: 'left',
                        transition: 'all 0.15s',
                      }}
                    >
                      {c.label}
                    </button>
                  );
                })}
              </div>

              {/* Textarea */}
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={5}
                placeholder={
                  category === 'content' ? "What's wrong? Be specific — set name, card number, what should be corrected..." :
                  category === 'feature' ? "Which set should we add? Year, brand, anything you know about it..." :
                  category === 'bug'     ? "What happened, and what did you expect to happen?" :
                                           "What's on your mind?"
                }
                style={{
                  width: '100%',
                  background: 'var(--panel)',
                  border: 'none',
                  borderTop: `1px solid ${C.border}`,
                  borderBottom: `1px solid ${C.border}`,
                  color: C.ink,
                  fontFamily: "'Share Tech Mono', monospace",
                  fontSize: '13px',
                  padding: '14px 20px',
                  resize: 'vertical',
                  outline: 'none',
                  boxSizing: 'border-box',
                  lineHeight: 1.6,
                  minHeight: '110px',
                }}
                autoFocus
              />

              {error && (
                <div style={{
                  padding: '10px 20px',
                  background: 'rgba(var(--red-rgb),0.1)',
                  borderBottom: `1px solid rgba(var(--red-rgb),0.3)`,
                  color: 'var(--red)',
                  fontFamily: "'Share Tech Mono', monospace",
                  fontSize: '12px',
                }}>
                  {error}
                </div>
              )}

              {/* Footer */}
              <div style={{
                padding: '12px 20px 16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
                flexWrap: 'wrap',
              }}>
                <span style={{
                  fontFamily: "'Share Tech Mono', monospace",
                  fontSize: '10.5px',
                  color: C.inkFaint,
                  flex: 1,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>
                  {user?.email ? `from ${user.email}` : 'anonymous'}
                </span>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={loading || !message.trim()}
                  style={{
                    fontFamily: "'Orbitron', monospace",
                    fontSize: '10.5px',
                    letterSpacing: '2px',
                    fontWeight: 700,
                    padding: '10px 18px',
                    background: message.trim() ? C.cyan : 'transparent',
                    border: `1px solid ${C.cyan}`,
                    color: message.trim() ? C.uiBg : C.cyan,
                    cursor: (loading || !message.trim()) ? 'not-allowed' : 'pointer',
                    borderRadius: '2px',
                    opacity: (loading || !message.trim()) ? 0.5 : 1,
                    flexShrink: 0,
                  }}
                >
                  {loading ? 'SENDING...' : 'SEND →'}
                </button>
              </div>
            </>
          )}

        </div>
      </div>
    </>
  );
}
